import { startTransition, useEffect, useMemo, useRef, useState } from "react";
import { Animated, PanResponder, View } from "@-/com.npmjs.react-native";
import { withUniwind } from "@-/com.npmjs.uniwind";
import useLatest from "@-/com.npmjs.use-latest";
import type { LoopingPagerProps } from "#looping-pager-types";
import { loopingPagerMod, loopingPagerSelectionDirection } from "#looping-pager-ring";
import { useLoopingPagerLoadedKeys } from "#use-looping-pager-loaded-keys";

const SWIPE_VELOCITY_THRESHOLD = 0.15;
const TRANSITION_DURATION_MS = 250;

/**
 * React Native's `Animated.View` drops `className` on web, which left every page in normal flow:
 * stacked below the pages before it instead of filling the pager.
 */
const AnimatedPage = withUniwind(Animated.View);

/**
 * Browsers have no native pager, so pages are translated around the ring: `position` counts
 * slots, and page `p` sits at its nearest slot congruent to `p`, so the page before the first is
 * the last. Each page is rendered once.
 */
export function LoopingPager({
  className,
  index,
  onIndexChange,
  pages,
  preloadDistance = 1,
  style,
  swipeEnabled = true,
}: LoopingPagerProps) {
  const count = pages.length;
  const [width, setWidth] = useState(0);
  const [position] = useState(() => new Animated.Value(index));
  // The slot the pager rests on or is animating to, and the page count it was chosen for.
  const slotRef = useRef(index);
  const slotCountRef = useRef(count);
  const isDraggingRef = useRef(false);
  // Bumped after a swipe settles, so a refused swipe animates back.
  const [swipeCount, setSwipeCount] = useState(0);
  const isLoaded = useLoopingPagerLoadedKeys(
    pages.map((page) => page.key),
    index,
    preloadDistance,
  );
  const latest = useLatest({ count, index, onIndexChange, swipeEnabled, width });

  useEffect(() => {
    if (count === 0 || isDraggingRef.current) {
      return;
    }
    const target = Math.min(Math.max(index, 0), count - 1);
    // Slots map to other pages once the count changes: restart from the selected page.
    if (width === 0 || slotCountRef.current !== count) {
      slotCountRef.current = count;
      slotRef.current = target;
      position.setValue(target);
      return;
    }
    const shown = loopingPagerMod(slotRef.current, count);
    if (shown === target) {
      return;
    }
    const direction = loopingPagerSelectionDirection(shown, target, count);
    slotRef.current += direction * loopingPagerMod(direction * (target - shown), count);
    Animated.timing(position, {
      duration: TRANSITION_DURATION_MS,
      toValue: slotRef.current,
      useNativeDriver: false,
    }).start();
  }, [count, index, position, swipeCount, width]);

  const panResponder = useMemo(() => {
    const finishSwipe = (step: number) => {
      slotRef.current += step;
      Animated.timing(position, {
        duration: TRANSITION_DURATION_MS,
        toValue: slotRef.current,
        useNativeDriver: false,
      }).start(() => {
        isDraggingRef.current = false;
        const page = loopingPagerMod(slotRef.current, latest.current.count);
        if (page !== latest.current.index) {
          latest.current.onIndexChange(page);
        }
        // After the selection it reports, so a refusal is seen with the index it left in place.
        startTransition(() => {
          setSwipeCount((previous) => previous + 1);
        });
      });
    };
    return PanResponder.create({
      onMoveShouldSetPanResponder: (_, gesture) =>
        latest.current.swipeEnabled &&
        latest.current.count > 1 &&
        latest.current.width > 0 &&
        Math.abs(gesture.dx) > 10 &&
        Math.abs(gesture.dx) > Math.abs(gesture.dy) * 2,
      onPanResponderGrant: () => {
        isDraggingRef.current = true;
        position.stopAnimation();
      },
      onPanResponderMove: (_, gesture) => {
        position.setValue(slotRef.current - gesture.dx / latest.current.width);
      },
      onPanResponderRelease: (_, gesture) => {
        const isPageTurn =
          Math.abs(gesture.dx) > latest.current.width / 2 ||
          Math.abs(gesture.vx) > SWIPE_VELOCITY_THRESHOLD;
        finishSwipe(isPageTurn ? -Math.sign(gesture.dx) : 0);
      },
      onPanResponderTerminate: () => {
        finishSwipe(0);
      },
    });
  }, [latest, position]);

  return (
    <View
      className={className === undefined ? "overflow-hidden" : `overflow-hidden ${className}`}
      onLayout={(event) => {
        setWidth(event.nativeEvent.layout.width);
      }}
      style={style}
      {...panResponder.panHandlers}
    >
      {pages.map((page, pageIndex) => {
        // This page's nearest slot relative to the position, in [-count / 2, count / 2).
        const offset = Animated.subtract(
          Animated.modulo(
            Animated.add(Animated.multiply(position, -1), pageIndex + count / 2),
            count,
          ),
          count / 2,
        );
        return (
          <AnimatedPage
            aria-hidden={pageIndex !== index}
            className="absolute inset-0"
            key={page.key}
            style={{ transform: [{ translateX: Animated.multiply(offset, width) }] }}
          >
            {isLoaded(page.key) ? page.render() : null}
          </AnimatedPage>
        );
      })}
    </View>
  );
}
